import { CLR_STATUSES } from './credit-limit-reviews'
import type { DoaRoleSlot } from './delegation-of-authority'

/**
 * Mass credit limit reviews (FUZE-612): one review that holds many accounts. The container
 * travels one DOA chain as a single request — Preparer → Approver, no reviewer since the phase 2
 * UAT (Oct 2026): the approver does the line-by-line pass and sends back to the preparer — and
 * has two states no single CLR has: `draft` (a run is prepared across multiple sittings — up to
 * 10k lines) and `cancelled` (a discarded draft kept as a record).
 */
export const MASS_CLR_STATUSES = ['draft', ...CLR_STATUSES, 'cancelled'] as const
export type MassClrRunStatus = (typeof MASS_CLR_STATUSES)[number]

/** The preparer can edit lines only while the run has not entered (or has been sent back from)
 *  the approval chain. */
export const MASS_CLR_EDITABLE_STATUSES = [
  'draft',
  'sent_back_to_preparer',
] as const satisfies readonly MassClrRunStatus[]

export function isMassClrRunEditable(status: MassClrRunStatus): boolean {
  return (MASS_CLR_EDITABLE_STATUSES as readonly MassClrRunStatus[]).includes(status)
}

/**
 * States where the run sits with a chain actor (`pendingSlot` is set). One state since the
 * reviewer slot went: the approver decides, comments, checks and excludes lines from here.
 */
export const MASS_CLR_CHAIN_STATUSES = ['in_review'] as const satisfies readonly MassClrRunStatus[]

export function isMassClrRunInChain(status: MassClrRunStatus): boolean {
  return (MASS_CLR_CHAIN_STATUSES as readonly MassClrRunStatus[]).includes(status)
}

/**
 * States where a run HOLDS its accounts against new mass runs and duplicate reviews. The mass
 * twin of `CLR_ACTIVE_STATUSES` (which stays single-CLR-only): a run sent back to the preparer
 * is still very much alive, and releasing its 10k accounts would invite double-reviews.
 */
export const MASS_CLR_ACTIVE_STATUSES = [
  'in_review',
  'sent_back_to_preparer',
] as const satisfies readonly MassClrRunStatus[]

/**
 * Why an account matched the selection filters but did not become a run line.
 *
 * `strategicRecommendation` reads the RECOMMENDED limit (client decision, see
 * docs/mass-clr/client-questions.md §13), the counterpart of single CLR's `isClrStrategicAccount`
 * testing the PROPOSED limit. It is resolved in the SAME pass for the
 * selection preview and for run creation, off the denormalized recommendation inputs, so the
 * count the preparer sees is the population that gets built.
 *
 * `ineligibleRiskClass` (Aug 13 client decision): Z01 provisional and Z07 blocked accounts never
 * enter a mass run, whatever their band.
 *
 * `unresolvableBand` has NO producer: mass DOA is one row, so there is no band left to fail to
 * resolve. It stays in the union because `exclusion_counts` is persisted jsonb on older runs and
 * the selection preview renders whatever reason keys come back; dropping the member would strand
 * those rows. Do not "clean it up".
 */
export const MASS_CLR_EXCLUSION_REASONS = [
  'alreadyInReview',
  'ineligibleRiskClass',
  'strategicRecommendation',
  'unresolvableBand',
  // A payer in a STARTED initiative: its limit carries a temporary uplift, and a CLR built from
  // BAU history would claw it back (FUZE-632).
  'activeInitiativeUplift',
  // Present-but-off-the-scale credit data (phase 2 UAT): the same predicates the customer
  // details page warns on (`hasInvalidRiskClass`, `hasInvalidCreditIndexScore`). A missing value
  // is not invalid and does not exclude.
  'invalidRiskClass',
  'invalidCreditIndexScore',
  // Created in SAP less than `NEW_ACCOUNT_MAX_AGE_MONTHS` ago (phase 2 UAT): too young for a
  // history-based review. Shown in the preview, never built into the run. An unknown creation
  // date does not exclude, since it cannot prove the account is young.
  'recentlyCreated',
] as const
export type MassClrExclusionReason = (typeof MASS_CLR_EXCLUSION_REASONS)[number]

export type MassClrExclusionCounts = Partial<Record<MassClrExclusionReason, number>>

/** Sort fields the selection-preview endpoint accepts — kept in lockstep with the table's
 *  column ids on the New Mass Review screen. */
export const MASS_CLR_SELECTION_SORT_FIELDS = [
  'erp',
  'businessName',
  'province',
  'currentCreditLimit',
  'currentRiskClass',
  'nextReviewDate',
  // The Status column. Sorted in memory off the shared classifier, not in SQL: the strategic
  // verdict needs the recommendation engine, which SQL cannot see.
  'status',
] as const
export type MassClrSelectionSortField = (typeof MASS_CLR_SELECTION_SORT_FIELDS)[number]

/** Everything a line can have done to it, singly or in bulk. The first four belong to the
 *  preparer; `exclude` is the approver's cut (Aug 20 decision, FUZE-616); `restore` brings a
 *  removed or excluded line back into the run (phase 2 UAT). */
export const MASS_CLR_LINE_PATCH_KINDS = [
  'deviate',
  'reset',
  'remove',
  'exclude',
  'restore',
] as const
export type MassClrLinePatchKind = (typeof MASS_CLR_LINE_PATCH_KINDS)[number]

/**
 * Append-only run history vocabulary. `created`/`submitted`/`cancelled` are written by the
 * preparer flow; the decisions and line cuts land with the approver.
 *
 * `reviewed` and `sent_back_to_reviewer` have had NO producer since the reviewer slot was removed
 * (phase 2 UAT, Oct 2026). They stay because `mass_credit_limit_review_actions` is append-only
 * history that already holds them on deployed databases; dropping the members would strand those
 * rows and the activity log that renders them. Do not "clean them up".
 */
export const MASS_CLR_ACTION_TYPES = [
  'created',
  'submitted',
  'reviewed',
  'approved',
  'rejected',
  'sent_back',
  'sent_back_to_reviewer',
  'lines_excluded',
  'lines_restored',
  'cancelled',
] as const
export type MassClrActionType = (typeof MASS_CLR_ACTION_TYPES)[number]

/**
 * The comment slots that satisfy the approver's gate: before approving, every in-run line must
 * carry a comment the APPROVER wrote in the current cycle. The preparer can comment lines too
 * (phase 2 UAT), but their notes are context for the approver, not a substitute for the
 * approver's own pass. One list, read by the server gate and the client counter.
 */
export const MASS_CLR_GATE_COMMENT_SLOTS = [
  'approver',
  'approver_delegate',
] as const satisfies readonly DoaRoleSlot[]

/**
 * The comment slots that satisfy the preparer's submit gate (phase 2 UAT, second round): before
 * submitting, every in-run line must carry a comment the PREPARER wrote — in any cycle, so a
 * resubmission after a send-back keeps the notes of the first pass.
 */
export const MASS_CLR_PREPARER_COMMENT_SLOTS = [
  'preparer',
  'preparer_delegate',
] as const satisfies readonly DoaRoleSlot[]

export const MASS_CLR_RUN_SCOPES = ['all', 'pending_my_action', 'mine'] as const
export type MassClrRunScope = (typeof MASS_CLR_RUN_SCOPES)[number]

/** Translatable business-rule failures (frontend renders `clr.errors.<key>`). */
export const MASS_CLR_ERRORS = [
  'massClrNotEligiblePreparer',
  'massClrInvalidState',
  'massClrEmptySelection',
  'massClrLinesNotCommented',
  'massClrLinesNotCommentedByPreparer',
  'massClrCannotExcludeAllLines',
  'massClrNoApproversConfigured',
  // A restore refused because every targeted account is held by another review by now.
  'massClrAccountHeldElsewhere',
  // Raised by the line patch schema; the same key as the single CLR so one translation serves both.
  'clrCreditLimitNotWhole',
] as const
export type MassClrErrorType = (typeof MASS_CLR_ERRORS)[number]

export function isMassClrErrorType(value: unknown): value is MassClrErrorType {
  return typeof value === 'string' && (MASS_CLR_ERRORS as readonly string[]).includes(value)
}

/**
 * A recommendation that moves the limit by at least this much (either direction) marks the line
 * `limit_swing` — the advisory "worth eyes before it goes up the chain" flag. A risk-class move
 * always flags regardless of magnitude. Never a gate.
 */
export const MASS_CLR_FLAGGED_SWING_PERCENT = 35

/** The calendar the credit team works in. A run created at 11 PM in Montréal is created "today"
 *  for them, not tomorrow (UTC), so the reference reads the date in this zone. */
export const CREDIT_BUSINESS_TIMEZONE = 'America/Toronto'

/**
 * Human-facing run reference, e.g. `MR-2026-08-30`: the creation date in the business timezone.
 * It used to carry the serial id (`MR-2026-014`), which read as a count nobody could place; the
 * date is what the preparer remembers. Deliberately NOT unique — two runs created the same day
 * share it, and their names tell them apart. The serial id stays the identity everywhere else.
 */
export function formatMassClrReference(createdAt: Date): string {
  // `en-CA` renders `YYYY-MM-DD` natively, which keeps the constants package free of date-fns-tz.
  const date = new Intl.DateTimeFormat('en-CA', {
    timeZone: CREDIT_BUSINESS_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(createdAt)
  return `MR-${date}`
}
